// Build packages (ContentIQ Phase B round 3) — pure helpers shared by the
// PM board, the Page build panel and the build team's queue. The backend
// computes status and the ball (services/packages.py); these only decide how
// a given viewer reads it: the same ball is "You" to the PM and "DemandArc"
// to the build team.

const HOLDER_LABEL = {
  pm: { pm: 'You', builder: 'DemandArc' },
  builder: { pm: 'Build team', builder: 'You' },
  client: { pm: 'Client', builder: 'Client' },
};

export function holderLabel(holder, perspective = 'pm', names = null) {
  if (names && names[holder]) return names[holder];
  return (HOLDER_LABEL[holder] || {})[perspective] || holder;
}

// "You" is the person reading; the other side is named (Edward, 2026-10-23).
// The build team reads its PM's name, a PM reading their own project reads
// "You" + the team's name, anyone else at DemandArc reads both names. No PM
// on the program falls back to "DemandArc". `row` is any package payload
// carrying pm_name / pm_user_id / team_name (the backend sends all three).
export function ballNames(row, { isTeam = false, currentUserId = null } = {}) {
  const pmName = row?.pm_name || 'DemandArc';
  const team = row?.team_name || 'Build team';
  if (isTeam) return { pm: pmName, builder: 'You', client: 'Client' };
  const readerIsPm = currentUserId != null && row?.pm_user_id != null
    && Number(currentUserId) === Number(row.pm_user_id);
  return { pm: readerIsPm ? 'You' : pmName, builder: team, client: 'Client' };
}

// The PM side, said to the reader: "You" when they are the project's PM.
export function pmSubject(row, currentUserId) {
  const readerIsPm = currentUserId != null && row?.pm_user_id != null
    && Number(currentUserId) === Number(row.pm_user_id);
  return readerIsPm ? 'You' : (row?.pm_name || 'DemandArc');
}

// "since yesterday" / "4 business days — overdue" — the backend sends the
// business-day count; the wording lives here.
export function ballAgeText(ball) {
  if (!ball || ball.business_days === null || ball.business_days === undefined) return '';
  const n = ball.business_days;
  if (ball.overdue) return `${n} business days — overdue`;
  if (n === 0) return 'since today';
  if (n === 1) return 'since yesterday';
  return `${n} business days`;
}

// PM board filter buckets: the PM's move first, then whoever else holds it.
export function pmBucket(row) {
  if (row.status === 'launched') return 'launched';
  const holders = (row.ball || []).map(b => b.holder);
  if (holders.includes('pm')) return 'yours';
  if (holders.includes('builder')) return 'builder';
  if (holders.includes('client')) return 'client';
  return 'yours';
}

// Build team's queue groups — a package split across both sides sits in
// "Needs you" whenever any page is theirs.
export function builderBucket(pkg) {
  if (pkg.status === 'launched') return 'launched';
  const holders = (pkg.ball || []).map(b => b.holder);
  return holders.includes('builder') ? 'needs_you' : 'with_demandarc';
}

// How long the ball has sat where it is — the oldest holder on a split
// package; -1 when there is no ball to age.
export function ballAgeDays(row) {
  return Math.max(-1, ...(row.ball || []).map(x => x.business_days ?? -1));
}

// Oldest overdue first within a bucket, then by age — what to chase.
export function byUrgency(a, b) {
  const od = r => (r.ball || []).some(x => x.overdue) ? 1 : 0;
  return (od(b) - od(a)) || (ballAgeDays(b) - ballAgeDays(a));
}

// A promotion-email build, however the backend marks it (the build's kind,
// or a spec shape on builds frozen before kind existed).
export function isEdmBuild(build) {
  return !!build && (build.kind === 'edm' || build.spec?.shape === 'edm');
}

// A build's name on every screen. Since 2026-10-22 a generated flow is one
// build per web page (a branching product's interactive page is ONE build
// for every path), so the page's own name leads: "Interactive qualification
// page", "Registration form — <asset>". Builds frozen before that carry no
// page_name and keep their asset title.
export function buildPageTitle(build) {
  const s = (build && build.spec) || {};
  const assetTitle = s.asset && s.asset.title;
  if (!s.page_name) return assetTitle || `Page ${s.page_number || (build && build.id) || ''}`.trim();
  if (s.page_set == null && s.shared_by) return s.page_name;
  return assetTitle && assetTitle !== s.page_name ? `${s.page_name} — ${assetTitle}` : s.page_name;
}

// The short line under a build's name: the page's place in the flow, whether
// every branch shares it or which branch it belongs to, and where review
// starts. Edward, 2026-10-29: one interactive Page 1, then the branches —
// every branch's registration form is Page 2, never "page 3 of 4". Specs
// frozen before that carry no flow_page and keep counting builds.
export function buildPageNote(spec) {
  const s = spec || {};
  const bits = [s.flow_page ? `Page ${s.flow_page}` : `page ${s.page_number || '?'} of ${s.page_count || '?'}`];
  if (s.page_set == null && s.shared_by) bits.push('one page for every branch');
  else if (s.lane_kind === 'branch') bits.push(`Branch ${s.lane_number}: ${s.lane_name || s.lane}`);
  else if (s.lane) bits.push(`path: ${s.lane}`);
  if (s.page_role === 'entry') bits.push('DemandArc starts the review here');
  return bits.join(' · ');
}

// The page statuses that are back with DemandArc for review, and the ones
// the build team holds (backend BUILDER_STATUSES).
export const IN_REVIEW_STATUSES = ['url_submitted', 'checked'];
export const HELD_STATUSES = ['assigned', 'corrections_requested'];

// The link the build team has ready for a page it holds: what they saved on
// the package, else — on a page sent back for corrections — the link they
// already submitted, since a fix usually keeps the same URL.
export function returnLinkFor(pkg, build) {
  const saved = (pkg && pkg.return_links) || {};
  return saved[build.id] || saved[String(build.id)]
    || (build.status === 'corrections_requested' ? build.url : '') || '';
}

// The steps a package moves through, for the progress strip. Client review
// is optional — skipped silently once launch is requested.
export const PACKAGE_STEPS = [
  { key: 'draft', label: 'Draft' },
  { key: 'sent', label: 'Sent' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'build', label: 'In build' },
  { key: 'review', label: 'Review' },
  { key: 'client_review', label: 'Client review (optional)' },
  { key: 'launch', label: 'Launch' },
  { key: 'launched', label: 'Launched' },
];

const STATUS_STEP = {
  draft: 'draft',
  sent: 'sent',
  in_build: 'build',
  corrections: 'build',
  in_review: 'review',
  review_corrections: 'review',
  complete: 'review',
  client_review: 'client_review',
  approved: 'client_review',
  launch_requested: 'launch',
  launched: 'launched',
};

// [{key, label, state: 'done'|'current'|'todo'|'skipped'}]. A build team can
// submit links without ever acknowledging; pass acknowledged=false so that
// step reads as skipped rather than earning a tick.
export function packageSteps(status, acknowledged = true) {
  const currentKey = STATUS_STEP[status] || 'draft';
  const idx = PACKAGE_STEPS.findIndex(s => s.key === currentKey);
  return PACKAGE_STEPS.map((s, i) => {
    let state = i < idx ? 'done' : i === idx ? (status === 'launched' ? 'done' : 'current') : 'todo';
    if (s.key === 'acknowledged' && state === 'done' && !acknowledged) state = 'skipped';
    return { ...s, state };
  });
}

// The downloaded package's file name — the same on the PM's and the build
// team's screens.
export function packageZipName(projectName, version) {
  const slug = String(projectName || 'package').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${slug || 'package'}-v${version}.zip`;
}

// A page's form fields as two lists: what the visitor fills in, and the
// hidden fields the page passes along (UTMs, campaign codes). The server
// stamps each field's placement; a field without one is visible.
export function splitFormFields(fields) {
  const all = fields || [];
  return {
    visible: all.filter(f => (f.placement || 'visible') === 'visible'),
    hidden: all.filter(f => f.placement === 'hidden'),
  };
}

// Today as YYYY-MM-DD in the viewer's own timezone — the default for a
// date the person is about to pick (the build team's review-links ETA).
export function todayLocalIso(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

